import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SubscriptionProvider } from "@/context/SubscriptionContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Buying from "@/pages/Buying";
import Staging from "@/pages/Staging";
import Scan from "@/pages/Scan";
import Shows from "@/pages/Shows";
import Selling from "@/pages/Selling";
import Trade from "@/pages/Trade";
import CsvCenter from "@/pages/CsvCenter";
import Labels from "@/pages/Labels";
import Reprice from "@/pages/Reprice";
import SalesLog from "@/pages/SalesLog";
import Sync from "@/pages/Sync";
import Team from "@/pages/Team";
import Settings from "@/pages/Settings";
import Billing from "@/pages/Billing";
import Admin from "@/pages/Admin";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentReturn";

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--pk-bg)" }}>
      <div className="h-10 w-10 rounded-full border-2 border-amber-500/30 border-t-amber-500 animate-spin" />
    </div>
  );
}

function Protected({ children }) {
  const { user, checked } = useAuth();
  const location = useLocation();
  if (!checked) return <Spinner />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function Home() {
  const { user, checked } = useAuth();
  if (!checked) return <Spinner />;
  return user ? <Navigate to="/inventory" replace /> : <Landing />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route path="/inventory" element={<Dashboard />} />
        <Route path="/buying" element={<Buying />} />
        <Route path="/boxed" element={<Buying boxedOnly />} />
        <Route path="/staging" element={<Staging />} />
        <Route path="/sell" element={<Selling />} />
        <Route path="/trade" element={<Trade />} />
        <Route path="/labels" element={<Labels />} />
        <Route path="/csv" element={<CsvCenter />} />
        <Route path="/reprice" element={<Reprice />} />
        <Route path="/analytics" element={<SalesLog />} />
        <Route path="/sales" element={<Navigate to="/analytics" replace />} />
        <Route path="/scan" element={<Scan />} />
        <Route path="/shows" element={<Shows />} />
        <Route path="/sync" element={<Sync />} />
        <Route path="/team" element={<Team />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/billing" element={<Billing />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SubscriptionProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </SubscriptionProvider>
        <Toaster position="bottom-right" theme="dark" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
